/**
 * The Tableau workbook's quick filters for Product dash. Ids and labels match the Lens spec in
 * svc-reporting (product-dash.json); the values come back from Lens. Every control is a
 * multi-select that starts empty, which means All, as Tableau's saved view does for these fields,
 * so the default numbers are unchanged. The controls apply to every sheet.
 */
export const SELECTION_FILTERS = [
  { id: 'brand', label: 'Brand Name' },
  { id: 'product', label: 'Product Name' },
  { id: 'domestic_international', label: 'Domestic/International' },
  { id: 'point_of_sale_country', label: 'Point of Sale Country' },
  { id: 'supply_country', label: 'Point of Supply Country' },
  { id: 'supply_region_1', label: 'Point of Supply Region 1' },
  { id: 'supply_region_2', label: 'Point of Supply Region 2' },
  { id: 'offer', label: 'Offer Name' },
  { id: 'offer_id', label: 'Offer Bk' },
  { id: 'market_manager', label: 'Offer Market Manager' },
  { id: 'pcm', label: 'PCM' },
  { id: 'surcharge', label: 'Is surcharge' },
  { id: 'chain', label: 'Chain Name' },
  { id: 'vendor', label: 'Vendor Name' },
] as const;

export type SelectionId = (typeof SELECTION_FILTERS)[number]['id'];
export type Selections = Partial<Record<SelectionId, Array<string>>>;

export const SELECTION_IDS: ReadonlyArray<SelectionId> = SELECTION_FILTERS.map((filter) => filter.id);

/** Item status keeps the workbook's saved set by default; Tableau also offers cancelled. */
export const ITEM_STATUSES = ['awaiting_dates', 'cancelled', 'completed'] as const;
export const DEFAULT_ITEM_STATUS: ReadonlyArray<string> = ['awaiting_dates', 'completed'];
export const ITEM_STATUS_KEY = 'pd_item_status';

/** The report card, and a one-row card requested with the dropdown values so neither the report nor the raw value rows load twice. */
export const REPORT_CARD_IDS = ['facts'];
export const OPTIONS_CARD_IDS = ['filter-scope'];

/** The options loader in svc-reporting returns at most this many values per control. */
export const OPTION_LIMIT = 500;

const URL_PREFIX = 'pd_f_';

export const selectionKey = (id: SelectionId) => `${URL_PREFIX}${id}`;

function readArray(raw: string | null): Array<string> | undefined {
  if (!raw) {
    return undefined;
  }
  try {
    const value: unknown = JSON.parse(raw);
    if (Array.isArray(value) && value.length && value.every((item) => typeof item === 'string')) {
      return [...new Set(value as Array<string>)];
    }
  } catch {
    // A hand-edited or truncated link falls back to the default for that control.
  }
  return undefined;
}

/** Values are stored in the URL as a JSON array per filter id, the same as Accommodation Views. */
export function readSelections(params: URLSearchParams): Selections {
  const selections: Selections = {};
  SELECTION_IDS.forEach((id) => {
    const values = readArray(params.get(selectionKey(id)));
    if (values) {
      selections[id] = values;
    }
  });
  return selections;
}

export function writeSelections(selections: Selections, current: URLSearchParams): URLSearchParams {
  const params = new URLSearchParams(current);
  SELECTION_IDS.forEach((id) => {
    const values = [...new Set(selections[id] ?? [])].sort();
    if (values.length) {
      params.set(selectionKey(id), JSON.stringify(values));
    } else {
      params.delete(selectionKey(id));
    }
  });
  return params;
}

/** Unknown statuses are dropped; a link with none left uses the workbook's saved set. */
export function readItemStatus(params: URLSearchParams): Array<string> {
  const values = (readArray(params.get(ITEM_STATUS_KEY)) ?? []).filter((value) =>
    (ITEM_STATUSES as ReadonlyArray<string>).includes(value),
  );
  return values.length ? values.sort() : [...DEFAULT_ITEM_STATUS];
}

/**
 * The saved set is left out of the URL. Clearing every status means All, the same as the other
 * controls, because an empty list would return no sales at all.
 */
export function writeItemStatus(values: ReadonlyArray<string>, current: URLSearchParams): URLSearchParams {
  const params = new URLSearchParams(current);
  const known = [...new Set(values)].filter((value) => (ITEM_STATUSES as ReadonlyArray<string>).includes(value)).sort();
  const next = known.length ? known : [...ITEM_STATUSES];
  if (next.join() === [...DEFAULT_ITEM_STATUS].sort().join()) {
    params.delete(ITEM_STATUS_KEY);
  } else {
    params.set(ITEM_STATUS_KEY, JSON.stringify(next));
  }
  return params;
}

/** Every key this module writes, so Reset filters can clear them. */
export function clearSelections(current: URLSearchParams): URLSearchParams {
  const params = new URLSearchParams(current);
  SELECTION_IDS.forEach((id) => params.delete(selectionKey(id)));
  params.delete(ITEM_STATUS_KEY);
  return params;
}

/**
 * The quick-filter part of the report request. A chain or vendor clicked in a table narrows the
 * sheet to that one row, so it replaces the Chain Name or Vendor Name selection while it is set;
 * the clicked row always comes from within that selection.
 */
export function selectionRequest(
  selections: Selections,
  clicked: { chain?: string; vendor?: string } = {},
): Record<string, Array<string>> {
  const request: Record<string, Array<string>> = {};
  SELECTION_IDS.forEach((id) => {
    if (selections[id]?.length) {
      request[id] = selections[id]!;
    }
  });
  if (clicked.chain) {
    request.chain = [clicked.chain];
  }
  if (clicked.vendor) {
    request.vendor = [clicked.vendor];
  }
  return request;
}

export function activeSelectionCount(selections: Selections, itemStatus: ReadonlyArray<string>): number {
  const statusChanged = [...itemStatus].sort().join() !== [...DEFAULT_ITEM_STATUS].sort().join();
  return SELECTION_IDS.filter((id) => selections[id]?.length).length + (statusChanged ? 1 : 0);
}

/** Read-only summary for page chat, keyed by the Tableau label. */
export function quickFilterSummary(selections: Selections, itemStatus: ReadonlyArray<string>) {
  return {
    'Item Status': [...itemStatus],
    ...Object.fromEntries(
      SELECTION_FILTERS.filter(({ id }) => selections[id]?.length).map(({ id, label }) => [label, selections[id]]),
    ),
  };
}
